/**
 * dashboard/api.ts — real data for the Closr dashboard.
 *
 * Replaces the prototype's lib/mockData.ts. Each fetcher calls a real platform
 * endpoint via the shared api client and maps the wire shape (src/api/types.ts)
 * to the dashboard view models (dashboard/types.ts). Fields that have no backing
 * endpoint are defaulted to 0 / '' / [] — never fabricated.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, apiFetch, ApiError, apiErrorMessage } from '../../api/client'
import { leadsQueueParams } from './pages/sales/leadsFlow'
import type { SavedView } from './pages/sales/savedViews'
import type {
  Agent, Run, RunsListResponse, ApprovalGate, SalesStats, SalesReports, SalesStageProbabilities, UpdateSalesStageProbabilitiesRequest,
  SalesQuotas, SalesQuota, UpsertSalesQuotaRequest, DeleteSalesQuotaRequest, SalesForecast, SalesPeriodKind,
  UpsertForecastOverrideRequest, SalesReportDefinition, SalesReportResult, DataExportList,
  SalesContact as WireContact, SalesDeal as WireDeal, SalesActivity as WireActivity,
  CreateContactRequest, CreateDealRequest, ConvertContactRequest,
  SalesAccount as WireAccount, SalesAccountListItem as WireAccountListItem,
  SalesAccountDetail as WireAccountDetail, SalesAccountInvoice,
  CreateAccountRequest, UpdateAccountRequest,
  SalesFieldHistoryEntry, SalesDealStageEvent, SalesMatchCandidate,
  UpdateDealRequest, UpdateContactRequest,
} from '../../api/types'
import type {
  SalesContact, SalesDeal, SalesActivity, DealStage, DealDetail, SalesSummary, SalesLead,
  SalesAccountRow, SalesAccountView,
  DashAgent, DashRun, DashSchedule, DashApproval,
  ApiHRCandidate, ApiHRJobPosting, CandidateStats, CreateCandidateRequest, HRTicket,
  ApiEmployee, ApiEmployeeDirectoryRow, ApiPayrollProfile, CreateEmployeeRequest, AddPayrollProfileRequest, NIPreview,
  ApiOrgDepartment, ApiOrgProfile, AddOrgProfileRequest, UpdateEmployeeIdentityRequest,
  LearningProvider, LearningProviderStandard, CreateProviderRequest, UpdateProviderRequest, AddStandardRequest,
  FinanceInvoice, FinanceExpense, FinanceStats, CreateExpenseRequest,
  InboundEmail, TenantDepartment, MeResponse,
  TenantSettings, TenantSettingsUpdate,
  IssuedInvoice, IssuedInvoiceCreate, NextInvoiceNumber,
  PayRun, PayrollPeriodResult, RecordPayrollPeriodRequest, RecordPayrollPeriodPreview,
  RestatePayrollPeriodRequest, RestatePayrollPeriodPreview,
  Payslip, PayslipPreview, PayslipDocument,
  Employment, EmploymentHistoryRow, P45, P45Preview, P45Document, P45SendStatus,
  OpsSop, SopWrite, OpsQualityCheck, QualityCheckWrite, OpsIncident, IncidentWrite,
  OpsAsset, AssetWrite, OpsTask, TaskWrite,
  OnboardingTemplate, OnboardingTemplateWrite, Onboarding,
} from './types'

// ── useAsync — minimal loading/error/data hook ─────────────────────────────────

export interface AsyncState<T> {
  data: T | null
  loading: boolean
  error: string | null
}

/**
 * Runs `fn` on mount (and when `deps` change), tracking loading/error/data.
 * Guards against setState-after-unmount via an `alive` flag.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null })
  useEffect(() => {
    let alive = true
    setState({ data: null, loading: true, error: null })
    fn()
      .then((d) => { if (alive) setState({ data: d, loading: false, error: null }) })
      // The fallback is the LITERAL string. Passing `e.message` here defeats
      // the helper: ApiError.message is `API ${status} ${statusText}` and
      // statusText is empty under HTTP/2, so the fallback evaluated to
      // "API 500 " — and since apiErrorMessage returns the fallback verbatim on
      // every 5xx, that is exactly what the whole dashboard rendered. It looks
      // like a defensive improvement and is the opposite of one; do not
      // reinstate it.
      .catch((e) => { if (alive) setState({ data: null, loading: false, error: apiErrorMessage(e, 'Failed to load') }) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  return state
}

// ── usePolledAsync — like useAsync, but re-fetches on an interval ───────────────

export interface PolledState<T> extends AsyncState<T> {
  /** Manually trigger an immediate (silent) re-fetch. */
  refresh: () => void
}

/**
 * Runs `fn` on mount and then every `intervalMs` while the tab is visible.
 * Only the initial load surfaces the loading state; subsequent polls update data
 * silently and keep the last good data on error (no flicker). `fn` is read from a
 * ref so inline closures don't resubscribe the interval; the effect re-subscribes
 * only when `deps` change.
 */
export function usePolledAsync<T>(fn: () => Promise<T>, intervalMs: number, deps: unknown[] = []): PolledState<T> {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null })
  const fnRef = useRef(fn)
  fnRef.current = fn
  const aliveRef = useRef(true)

  const run = useCallback(async (initial: boolean) => {
    if (initial) setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const d = await fnRef.current()
      if (aliveRef.current) setState({ data: d, loading: false, error: null })
    } catch (e) {
      // Keep the last good data on a poll failure; only surface the error text.
      if (aliveRef.current) {
        setState((s) => ({ data: s.data, loading: false, error: apiErrorMessage(e, 'Failed to load') })) // literal fallback — see useAsync
      }
    }
  }, [])

  useEffect(() => {
    aliveRef.current = true
    void run(true)
    const id = window.setInterval(() => {
      if (!document.hidden) void run(false)
    }, intervalMs)
    return () => {
      aliveRef.current = false
      window.clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const refresh = useCallback(() => { void run(false) }, [run])
  return { ...state, refresh }
}

// ── time/format helpers ────────────────────────────────────────────────────────

export function relAgo(iso?: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return '—'
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  if (m < 1440) return `${Math.floor(m / 60)}h ago`
  return `${Math.floor(m / 1440)}d ago`
}

/**
 * Bare future duration for "Next Run In" cells: "45m", "3h", "2d". Returns
 * "due" once the timestamp has passed and "—" when null — a NULL next_run_at
 * means the runner hasn't seeded the schedule yet (it does so within one poll
 * tick of creation/re-enable) or the schedule is paused/disabled.
 * Bare (no "in" prefix) so both the column and the "Next run: X in Y" banner
 * read naturally.
 */
export function relIn(iso?: string | null): string {
  if (!iso) return '—'
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms)) return '—'
  if (ms <= 0) return 'due'
  const m = Math.ceil(ms / 60000)
  if (m < 60) return `${m}m`
  if (m < 1440) return `${Math.floor(m / 60)}h`
  return `${Math.floor(m / 1440)}d`
}

function daysSince(iso?: string | null): number {
  if (!iso) return 0
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms) || ms < 0) return 0
  return Math.floor(ms / 86400000)
}

export function fmtDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Date AND time of day ("12 Sep 2026, 12:13") — for a record whose minute
 *  matters (an export's start; §31.4). fmtDate drops the time on purpose for
 *  the day-granular fields; this one is the same locale with the clock kept. */
export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function durationBetween(start?: string | null, end?: string | null): string {
  if (!start) return '—'
  const endMs = end ? new Date(end).getTime() : Date.now()
  const ms = endMs - new Date(start).getTime()
  if (Number.isNaN(ms) || ms < 0) return '—'
  const total = Math.floor(ms / 1000)
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`
}

// ── Sales stage mapping (platform slug → verbatim label, 1:1) ───────────────────
//
// The 0097 stage vocabulary matches the mockup's columns exactly (design note
// §A.2), so the old ordinal STAGE_TO_VIEW map is gone: each slug derives its
// label 1:1. An unrecognised slug lands in the named 'Unknown' bucket — never
// silently in a real stage.

// Exported (P10): the Dashboard funnel rows and the Reports FUNNEL_LABELS
// alias both read this one map — labels cannot drift per-page.
export const SLUG_TO_STAGE: Record<string, DealStage> = {
  new_lead:      'New Lead',
  qualified:     'Qualified',
  proposal_sent: 'Proposal Sent',
  negotiation:   'Negotiation',
  closed_won:    'Closed Won',
  closed_lost:   'Closed Lost',
}

// The write-side inverse, derived so the two maps cannot drift. 'Unknown' has
// no slug on purpose — it is a read-side bucket, never something to send.
export const STAGE_TO_SLUG = Object.fromEntries(
  Object.entries(SLUG_TO_STAGE).map(([slug, stage]) => [stage, slug]),
) as Partial<Record<DealStage, string>>

const ACTIVITY_TYPE_TO_VIEW: Record<string, SalesActivity['type']> = {
  email: 'email',
  call: 'call',
  meeting: 'meeting',
  note: 'note',
  task: 'task',
  linkedin_message: 'note',
}

// ── mappers (wire → view) ──────────────────────────────────────────────────────

export function mapContact(c: WireContact): SalesContact {
  return {
    id: c.id,
    name: c.fullName,
    company: c.company ?? '',
    accountId: c.accountId || undefined,
    // §25 L-7: the server-resolved name rides with the id; never derived here.
    accountName: c.accountName || undefined,
    accountLinkCleared: c.accountLinkCleared || undefined,
    title: c.title ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    status: (['new', 'contacted', 'qualified', 'unqualified'].includes(c.status)
      ? c.status
      : 'new') as SalesContact['status'],
    lastContacted: relAgo(c.lastContactedAt),
    linkedinUrl: c.linkedInUrl,
    enriched: Boolean(c.linkedInUrl) || Boolean(c.linkedInData),
    // §25 L-6: the server counts deals carrying this contact (any stage).
    // A missing key is an older backend → null → "—" on screen; a real 0
    // stays 0. Never fabricate a number the wire did not send.
    deals: c.dealCount ?? null,
    location: c.location ?? '',
    category: c.category ?? '',
    prospectSegment: c.prospectSegment ?? '',
    // E2: the contact editor prefills these.
    source: c.source ?? '',
    notes: c.notes ?? '',
    assignedEmployeeId: c.assignedEmployeeId || undefined,
    // D3: the inline owner cell renders the server-derived name.
    assignedTo: c.assignedTo || undefined,
  }
}

export function mapDeal(d: WireDeal): SalesDeal {
  return {
    id: d.id,
    name: d.title,
    owner: d.assignedTo ?? '—',
    company: d.company ?? '',
    accountId: d.accountId || undefined,
    // §25 L-11a: present only on the create response, and only when non-empty.
    ...(d.linkedContacts && d.linkedContacts.length > 0 ? { linkedContacts: d.linkedContacts } : {}),
    // Pence, from the D9 money leg; the pounds fallback covers a reader ahead
    // of a not-yet-upgraded serving path and is exact for whole pence.
    amount: d.valuePence ?? Math.round((d.value ?? 0) * 100),
    currency: d.currency ?? 'GBP',
    // The comparable leg (0111): GBP rows compare as themselves, non-GBP
    // rows by their captured base — null (uncaptured) drops out of sums.
    basePence: (d.currency ?? 'GBP') === 'GBP'
      ? (d.valuePence ?? Math.round((d.value ?? 0) * 100))
      : (d.baseValuePence ?? null),
    closeDate: fmtDate(d.expectedClose),
    stage: SLUG_TO_STAGE[d.stage] ?? 'Unknown',
    probability: d.probability ?? 0,
    // §23 D-4 / D-5: the weighted drill weights by the SERVER-resolved
    // probability and the cycle drill measures from the creation stamp —
    // both ride through verbatim, never derived here.
    effectiveProbability: d.effectiveProbability,
    createdAt: d.createdAt,
    lastActivity: relAgo(d.updatedAt),
    // P2-12 (2.4): the card's age reads the 0097 stage stamp, like Detail —
    // updatedAt bumps on every edit and could disagree with the server's
    // "Nd in stage" label on the very same card. Falls back for a pre-0097 wire.
    daysInStage: daysSince(d.stageChangedAt ?? d.updatedAt),
    // 2.4: the server's health verdict rides through verbatim.
    health: d.health,
    closedDate: d.closedAt ? fmtDate(d.closedAt) : '',
    closedAt: d.closedAt,
    lostReason: d.lostReason ?? '',
    issuedInvoiceId: d.issuedInvoiceId,
  }
}

/** The per-deal detail mapper. Unlike mapDeal it keeps contactId / notes /
 *  source / priority / lostReason. daysInStage derives from stageChangedAt
 *  (the 0097 stamp) rather than updatedAt, which bumps on every notes save —
 *  since 2.4 the card mapper does the same. Falls back to updatedAt only
 *  when the wire predates the stamp. */
export function mapDealDetail(d: WireDeal): DealDetail {
  return {
    id: d.id,
    title: d.title,
    company: d.company ?? '',
    accountId: d.accountId || undefined,
    contactId: d.contactId || undefined,
    stage: SLUG_TO_STAGE[d.stage] ?? 'Unknown',
    amountPence: d.valuePence ?? Math.round((d.value ?? 0) * 100),
    currency: d.currency ?? 'GBP',
    // 2.2: the OWN value stays nullable (null = not assessed); the effective
    // value and its source ride through verbatim — never re-derived here.
    probability: d.probability ?? null,
    effectiveProbability: d.effectiveProbability,
    probabilitySource: d.probabilitySource,
    // 2.3a: absent on the wire = NULL = Uncategorised. Normalised to null
    // HERE so every consumer has one shape to render, never `undefined`
    // meaning two different things.
    forecastCategory: d.forecastCategory ?? null,
    // 2.3c: the override (absent = none → null), the effective category
    // (absent = Uncategorised → null), its source and the gap — all
    // server-resolved, none re-derived here.
    forecastOverride: d.forecastOverride ?? null,
    effectiveForecastCategory: d.effectiveForecastCategory ?? null,
    forecastCategorySource: d.forecastCategorySource,
    forecastGapPence: d.forecastGapPence,
    expectedCloseLabel: fmtDate(d.expectedClose),
    // E2: the raw values the editor prefills from, beside their display forms.
    expectedClose: d.expectedClose || undefined,
    owner: d.assignedTo ?? '',
    assignedEmployeeId: d.assignedEmployeeId || undefined,
    source: d.source ?? '',
    priority: d.priority ?? '',
    notes: d.notes ?? '',
    lostReason: d.lostReason ?? '',
    lostReasonNote: d.lostReasonNote ?? '',
    daysInStage: daysSince(d.stageChangedAt ?? d.updatedAt),
    stageChangedAt: d.stageChangedAt,
    // 2.4: verbatim — the detail page lists every signal with its `since`.
    health: d.health,
    lastActivityAt: d.lastActivityAt,
    closedAt: d.closedAt,
    issuedInvoiceId: d.issuedInvoiceId,
    // The FX capture trio (SB-6): carried through verbatim for the detail
    // view's provenance line; absent on GBP rows.
    fxRate: d.fxRate,
    fxRateDate: d.fxRateDate,
    baseValuePence: d.baseValuePence,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
  }
}

export function mapActivity(a: WireActivity): SalesActivity {
  return {
    id: a.id,
    type: ACTIVITY_TYPE_TO_VIEW[a.activityType] ?? 'task',
    contact: a.subject, // wire activity carries no contact name; subject is the title
    company: '',
    description: a.body ?? '',
    time: relAgo(a.dueAt ?? a.createdAt),
    owner: a.assignedTo ?? '—',
    done: a.status === 'done' || Boolean(a.completedAt),
    deal: a.dealId || undefined,
  }
}

// ── fetchers ───────────────────────────────────────────────────────────────────

interface ListResp<T> { items?: T[] | null }

export async function fetchContacts(): Promise<SalesContact[]> {
  const r = await api.get<ListResp<WireContact>>('/v1/sales/contacts', { params: { limit: 200 } })
  return (r.items ?? []).map(mapContact)
}

export async function createContact(body: CreateContactRequest): Promise<SalesContact> {
  const wire = await api.post<WireContact>('/v1/sales/contacts', body)
  return mapContact(wire)
}

// ── Saved list views (Slice D / D1 backend; D2 UI) ───────────────────────────
// GET returns a BARE SavedView[] (the Go handler WriteJSONs the slice directly);
// per-user + tenant-scoped + object allowlist are all enforced server-side, so
// the client passes only the object and the view body. Update is PUT /{id}.

/** The create/update body — the shape stateToBody() produces. */
export interface SavedViewBody {
  object?: string
  name: string
  /** Opaque to the server: a list surface stores field → value strings; the
   *  report builder (2.5, object 'sales.reports') stores its whole
   *  definition here. */
  filters?: Record<string, unknown>
  columns?: string[]
  sort?: { field: string; dir: 'asc' | 'desc' }
  isDefault?: boolean
}

export async function fetchSavedViews(object: string): Promise<SavedView[]> {
  // The Go handler wraps the list in ListResponse ({items, page}) and a nil
  // slice marshals to {"items": null} — so unwrap and default, NEVER treat the
  // response as a bare array (that crashed the list pages on first prod use).
  const r = await api.get<ListResp<SavedView>>('/v1/ui/views', { params: { object } })
  return r.items ?? []
}

export async function createSavedView(body: SavedViewBody): Promise<SavedView> {
  return api.post<SavedView>('/v1/ui/views', body)
}

export async function updateSavedView(id: string, body: SavedViewBody): Promise<SavedView> {
  return api.put<SavedView>(`/v1/ui/views/${encodeURIComponent(id)}`, body)
}

export async function deleteSavedView(id: string): Promise<void> {
  await api.delete(`/v1/ui/views/${encodeURIComponent(id)}`)
}

/** Optional server-side filters (both already live on the endpoint):
 *  `stage` is a slug; `assignedTo` must be the EXACT owner string —
 *  the SQL is bare equality, the same match the Reports GROUP BY used. */
export async function fetchDeals(filters?: {
  stage?: string; assignedTo?: string; assignedEmployeeId?: string
  /** §33 W-2/W-3: the close-date window behind the Forecast grid's won
   *  drill (2.3b F-7 on the server). ISO dates; the server reads closedTo
   *  as a whole DAY (`closed_at < closedTo + 1 day`), so the caller sends
   *  the period's LAST day (`periodEndInclusive`), never its exclusive end. */
  closedFrom?: string; closedTo?: string
}): Promise<SalesDeal[]> {
  const params: Record<string, string | number> = { limit: 200 }
  if (filters?.stage) params.stage = filters.stage
  // The E360-2 owner filter PAIR: an employee id selects a LINKED owner's
  // rows; the text selects the UNLINKED bucket (text match AND no link) —
  // exactly the two populations Top performers groups by, so a drill keys on
  // whichever one its performer row carried.
  if (filters?.assignedTo) params.assigned_to = filters.assignedTo
  if (filters?.assignedEmployeeId) params.assigned_employee_id = filters.assignedEmployeeId
  if (filters?.closedFrom) params.closed_from = filters.closedFrom
  if (filters?.closedTo) params.closed_to = filters.closedTo
  const r = await api.get<ListResp<WireDeal>>('/v1/sales/deals', { params })
  return (r.items ?? []).map(mapDeal)
}

/** Archive (§5.F): the closed-deal list — TWO stage-filtered calls, never a
 *  client filter of the limit-200 unfiltered list (quietly wrong once open
 *  deals crowd the cap). Newest close first; a row missing closedAt (none
 *  should exist post-3a) sorts last rather than lying about recency. */
export async function fetchClosedDeals(): Promise<SalesDeal[]> {
  const [won, lost] = await Promise.all([
    api.get<ListResp<WireDeal>>('/v1/sales/deals', { params: { stage: 'closed_won', limit: 200 } }),
    api.get<ListResp<WireDeal>>('/v1/sales/deals', { params: { stage: 'closed_lost', limit: 200 } }),
  ])
  return [...(won.items ?? []), ...(lost.items ?? [])]
    .sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? ''))
    .map(mapDeal)
}

export async function createDeal(body: CreateDealRequest): Promise<SalesDeal> {
  const wire = await api.post<WireDeal>('/v1/sales/deals', body)
  return mapDeal(wire)
}

export async function fetchActivities(): Promise<SalesActivity[]> {
  const r = await api.get<ListResp<WireActivity>>('/v1/sales/activities', { params: { limit: 200 } })
  return (r.items ?? []).map(mapActivity)
}

// ── Deal Detail (per-deal page) ────────────────────────────────────────────────

export async function fetchDeal(id: string): Promise<DealDetail> {
  const wire = await api.get<WireDeal>(`/v1/sales/deals/${encodeURIComponent(id)}`)
  return mapDealDetail(wire)
}

export async function fetchContact(id: string): Promise<SalesContact> {
  const wire = await api.get<WireContact>(`/v1/sales/contacts/${encodeURIComponent(id)}`)
  return mapContact(wire)
}

// ── Inbound Leads (§8.F — a lead IS a contact, L8-1) ──────────────────────────

// ── Slice A (Phase 1 / 1.1): accounts ──────────────────────────────────────

export function mapAccount(a: WireAccount): SalesAccountView {
  return {
    id: a.id,
    name: a.name,
    website: a.website ?? '',
    phone: a.phone ?? '',
    notes: a.notes ?? '',
    createdAt: a.createdAt,
    updatedAt: a.updatedAt,
  }
}

/** List row: every number is the server's (listAccountsSQL) — nothing is
 *  summed client-side. openValuePence is the FENCED GBP-comparable sum. */
export function mapAccountRow(a: WireAccountListItem): SalesAccountRow {
  return {
    ...mapAccount(a),
    contactCount: a.contactCount ?? 0,
    openDealCount: a.openDealCount ?? 0,
    openValuePence: a.openValueBasePence ?? 0,
    openValueExcluded: a.openValueExcluded ?? 0,
    lastActivity: a.lastActivityAt ? relAgo(a.lastActivityAt) : '—',
    lastActivityAt: a.lastActivityAt,
  }
}

/** The 360 view: the header plus the four linked lists, each through the
 *  list mapper it already has. Activities stay on the wire shape (the Deal
 *  Detail timeline precedent). Go nil slices arrive as null → []. */
export interface SalesAccount360 {
  account: SalesAccountView
  contacts: SalesContact[]
  openDeals: SalesDeal[]
  closedDeals: SalesDeal[]
  activities: WireActivity[]
  invoices: SalesAccountInvoice[]
}

export function mapAccount360(d: WireAccountDetail): SalesAccount360 {
  return {
    account: mapAccount(d.account),
    contacts: (d.contacts ?? []).map(mapContact),
    openDeals: (d.openDeals ?? []).map(mapDeal),
    closedDeals: (d.closedDeals ?? []).map(mapDeal),
    activities: d.activities ?? [],
    invoices: d.invoices ?? [],
  }
}

/** GET /v1/sales/accounts — a BARE array (not the {items} envelope the other
 *  sales lists use). `search` is the server's name_normalised LIKE; the
 *  server caps limit at 500 and orders by name. */
export async function fetchAccounts(search?: string, limit = 200): Promise<SalesAccountRow[]> {
  const params: Record<string, string> = { limit: String(limit) }
  if (search?.trim()) params.search = search.trim()
  const r = await api.get<WireAccountListItem[] | null>('/v1/sales/accounts', { params })
  return (r ?? []).map(mapAccountRow)
}

export async function fetchAccount(id: string): Promise<SalesAccount360> {
  const wire = await api.get<WireAccountDetail>(`/v1/sales/accounts/${encodeURIComponent(id)}`)
  return mapAccount360(wire)
}

export async function createAccount(body: CreateAccountRequest): Promise<SalesAccountView> {
  const wire = await api.post<WireAccount>('/v1/sales/accounts', body)
  return mapAccount(wire)
}

/** PATCH: an omitted field keeps its value, "" clears it. */
export async function updateAccount(id: string, body: UpdateAccountRequest): Promise<SalesAccountView> {
  const wire = await api.patch<WireAccount>(`/v1/sales/accounts/${encodeURIComponent(id)}`, body)
  return mapAccount(wire)
}

// ── Slice B (0117) — the two history reads ─────────────────────────────────
// Both return the {items} envelope, and the server sends `"items": null` for
// an empty result rather than []. The `?? []` is load-bearing, not defensive
// noise: without it every consumer throws on a record that has no history —
// which is EVERY record created before 0117.

/** One record's NON-stage field history, newest first. */
export async function fetchFieldHistory(
  recordType: 'deals' | 'contacts' | 'accounts',
  id: string,
): Promise<SalesFieldHistoryEntry[]> {
  const r = await api.get<ListResp<SalesFieldHistoryEntry>>(
    `/v1/sales/${recordType}/${encodeURIComponent(id)}/history`)
  return r.items ?? []
}

/** One deal's stage transitions, newest first. Kept SEPARATE from the field
 *  history read on purpose (B-1) — the strip merges them for display only. */
export async function fetchDealStageEvents(id: string): Promise<SalesDealStageEvent[]> {
  const r = await api.get<ListResp<SalesDealStageEvent>>(
    `/v1/sales/deals/${encodeURIComponent(id)}/stage-events`)
  return r.items ?? []
}

// ── Slice C1/C2 — the duplicate guard's pre-flight ─────────────────────────
// GET /v1/sales/matches. ADVISORY (C-2): a caller must never gate a write on
// this read's success — an outage here must not stop a rep converting a lead
// (C2.4). Empty params are omitted so the server sees exactly what was asked;
// `items` is null on an empty result, as with every sales list.

export interface SalesMatchesQuery {
  email?: string
  company?: string
  excludeContactId?: string
  limit?: number
}

export async function fetchSalesMatches(q: SalesMatchesQuery): Promise<SalesMatchCandidate[]> {
  const params: Record<string, string> = {}
  if (q.email?.trim()) params.email = q.email.trim()
  if (q.company?.trim()) params.company = q.company.trim()
  if (q.excludeContactId) params.excludeContactId = q.excludeContactId
  if (q.limit && q.limit > 0) params.limit = String(q.limit)
  if (!params.email && !params.company) return []
  const r = await api.get<ListResp<SalesMatchCandidate>>('/v1/sales/matches', { params })
  return r.items ?? []
}

/** The 409 an account create returns when the name is already taken in the
 *  tenant carries the existing account's id (dispatchAccountCommand); null
 *  for any other error, so callers fall through to the generic toast. */
export function conflictingAccountId(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null
  try {
    const parsed = JSON.parse(e.body) as { error?: { existingAccountId?: string } }
    return parsed.error?.existingAccountId || null
  } catch {
    return null
  }
}

/** C3: the 409 a create or convert returns when the server refuses as a
 *  possible duplicate carries the matcher's `candidates` beside `message`
 *  inside `error` — the SAME item shape as GET /v1/sales/matches, so the
 *  chooser renders it unchanged. null for any other error, including the
 *  account exact-name 409 (which carries `existingAccountId`, not a list),
 *  so callers fall through to the generic toast. Malformed items are
 *  dropped, and an empty list is not a refusal-with-choice — it reads as
 *  null too. The message itself is read by apiErrorMessage as usual. */
export function candidatesFrom(e: unknown): SalesMatchCandidate[] | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(e.body)
  } catch {
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) return null
  const err = (parsed as { error?: unknown }).error
  if (typeof err !== 'object' || err === null) return null
  const list = (err as { candidates?: unknown }).candidates
  if (!Array.isArray(list)) return null
  const out = list.filter(isMatchCandidate)
  return out.length > 0 ? out : null
}

function isMatchCandidate(x: unknown): x is SalesMatchCandidate {
  if (typeof x !== 'object' || x === null) return false
  const c = x as Record<string, unknown>
  return typeof c.id === 'string' && c.id !== ''
    && (c.type === 'contact' || c.type === 'account')
    && typeof c.displayName === 'string'
    && typeof c.rule === 'string'
    && typeof c.score === 'number'
}

/** A2.3 link path on an existing deal: PUT /deals/{id} with the accountId
 *  and the account's name copied into company — the module validates the
 *  account in-tenant and would copy the name itself only when company is
 *  empty, so the explicit copy keeps the two in step on the wire. */
export async function linkDealAccount(dealId: string, accountId: string, company: string): Promise<DealDetail> {
  const wire = await api.put<WireDeal>(`/v1/sales/deals/${encodeURIComponent(dealId)}`, { accountId, company })
  return mapDealDetail(wire)
}

export function mapLead(c: WireContact): SalesLead {
  return {
    id: c.id,
    name: c.fullName,
    company: c.company ?? '',
    accountId: c.accountId || undefined,
    accountName: c.accountName || undefined,
    title: c.title ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    status: (['new', 'contacted', 'qualified', 'unqualified'].includes(c.status)
      ? c.status
      : 'new') as SalesLead['status'],
    source: c.source ?? '',
    assignedTo: c.assignedTo ?? '',
    assignedEmployeeId: c.assignedEmployeeId || undefined,
    location: c.location ?? '',
    notes: c.notes ?? '',
    linkedinUrl: c.linkedInUrl,
    createdAt: c.createdAt,
    createdLabel: fmtDate(c.createdAt),
    convertedDealId: c.convertedDealId ?? '',
  }
}

// ── Slice E — the editing surfaces' writers ─────────────────────────────────
// Both PUTs carry ONLY the fields the editor changed (dealEdit.ts /
// contactEdit.ts compute the diff), so one intended edit yields one history
// row per field actually changed and never a row per field on the form.

/** PUT /v1/sales/deals/{id} with a changed-fields-only body. On a closed deal
 *  the server refuses value/currency/expectedClose/probability/priority with a
 *  named 409 (E-2); the editor hides those there, so this is the backstop. */
export async function updateDeal(id: string, body: UpdateDealRequest): Promise<DealDetail> {
  const wire = await api.put<WireDeal>(`/v1/sales/deals/${encodeURIComponent(id)}`, body)
  return mapDealDetail(wire)
}

/** PUT /v1/sales/contacts/{id} with a changed-fields-only body, mapped to the
 *  CONTACT view (the drawer's shape). updateLead above is the lead-shaped
 *  sibling and stays separate (E-3). */
export async function updateContact(id: string, body: UpdateContactRequest): Promise<SalesContact> {
  const wire = await api.put<WireContact>(`/v1/sales/contacts/${encodeURIComponent(id)}`, body)
  return mapContact(wire)
}

/** SB-2 lead edit: the PUT carries only the fields the drawer sends —
 *  CASE-WHEN keep semantics server-side mean '' = unchanged, so the TEXT
 *  fields are SET-ONLY. The one exception (§25 L-2) is accountId, a
 *  nullable pointer: `null` clears the link. Response maps like any lead
 *  read. */
export async function updateLead(id: string, body: UpdateContactRequest): Promise<SalesLead> {
  const wire = await api.put<WireContact>(`/v1/sales/contacts/${encodeURIComponent(id)}`, body)
  return mapLead(wire)
}

/** The Inbound Leads queue: server-side L8-2 predicate — status 'new' AND
 *  unconverted (converted=false, the L1 rider). Both filters are server-side
 *  on purpose (200-cap honesty): never a client filter of the unfiltered
 *  list. Params built by leadsQueueParams so vitest pins the composition. */
export async function fetchInboundLeads(): Promise<SalesLead[]> {
  const r = await api.get<ListResp<WireContact>>('/v1/sales/contacts', { params: leadsQueueParams() })
  return (r.items ?? []).map(mapLead)
}

export async function fetchLead(id: string): Promise<SalesLead> {
  const wire = await api.get<WireContact>(`/v1/sales/contacts/${encodeURIComponent(id)}`)
  return mapLead(wire)
}

/** Convert a queued lead into a pipeline deal (POST …/convert, §8.D). The
 *  deal is born at new_lead on the DB default; re-convert is idempotent
 *  server-side (the same deal comes back). Returns the WIRE deal — the
 *  caller navigates to it via selectedDealId. */
export async function convertLead(id: string, body: ConvertContactRequest): Promise<WireDeal> {
  return api.post<WireDeal>(`/v1/sales/contacts/${encodeURIComponent(id)}/convert`, body)
}

/** Promote (→ contacted) / disqualify (→ unqualified): plain status writes
 *  on the contact (§8.E — the route EXISTS; partial-update wire, so only
 *  status is sent). Either exits the L8-2 queue. */
export async function updateLeadStatus(id: string, status: 'contacted' | 'unqualified'): Promise<SalesLead> {
  const wire = await api.put<WireContact>(`/v1/sales/contacts/${encodeURIComponent(id)}`, { status })
  return mapLead(wire)
}

/** The deal-scoped activity list, WIRE-shaped: the detail page needs subject/
 *  status/createdAt raw (tasks checklist + timeline), which the view
 *  mapActivity folds away. Param is snake_case on the wire
 *  (sales_handler.go reads deal_id). */
export async function fetchDealActivities(dealId: string): Promise<WireActivity[]> {
  const r = await api.get<ListResp<WireActivity>>('/v1/sales/activities',
    { params: { deal_id: dealId, limit: 200 } })
  return r.items ?? []
}

export async function createDealActivity(body: {
  dealId: string
  activityType: string
  subject: string
  body?: string
  contactId?: string
}): Promise<WireActivity> {
  return api.post<WireActivity>('/v1/sales/activities', body)
}

export async function completeActivity(id: string): Promise<WireActivity> {
  return api.post<WireActivity>(`/v1/sales/activities/${encodeURIComponent(id)}/complete`, {})
}

/** Un-tick: the complete route is one-way; reopening is a status update. */
export async function reopenActivity(id: string): Promise<WireActivity> {
  return api.put<WireActivity>(`/v1/sales/activities/${encodeURIComponent(id)}`, { status: 'pending' })
}

/** Save the Notes card. PARTIAL-update wire: an empty string is a silent
 *  no-op server-side (module.go's CASE WHEN != ''), so callers must not
 *  offer an empty save — clear-to-null is a later backend slice. */
export async function updateDealNotes(id: string, notes: string): Promise<DealDetail> {
  const wire = await api.put<WireDeal>(`/v1/sales/deals/${encodeURIComponent(id)}`, { notes })
  return mapDealDetail(wire)
}

/** Open-stage moves only: the server refuses closed stages here (decision
 *  #7) — closing goes through closeDeal below. Returns the WIRE deal:
 *  callers map to their own view model (board mapDeal, detail
 *  mapDealDetail). */
export async function moveDealStage(id: string, stageSlug: string): Promise<WireDeal> {
  return api.post<WireDeal>(`/v1/sales/deals/${encodeURIComponent(id)}/stage`, { stage: stageSlug })
}

/** The ONLY path into closed_won/closed_lost (§5.C: POST /close). lostReason
 *  is sent ONLY on a lost close — the server names a 400 for it on a won
 *  close (S2-4). Returns the WIRE deal; on a won close it carries the
 *  minted draft's issuedInvoiceId. */
/** SB-1: lostReason is a SLUG (vocab.ts owns the options + labels); the
 *  note rides only when non-empty — the server refuses a note without a
 *  slug, and requires one when the slug is 'other'. */
export async function closeDeal(id: string, outcome: 'won' | 'lost', lostReason?: string, lostReasonNote?: string): Promise<WireDeal> {
  return api.post<WireDeal>(`/v1/sales/deals/${encodeURIComponent(id)}/close`,
    { outcome, ...(lostReason ? { lostReason } : {}), ...(lostReasonNote ? { lostReasonNote } : {}) })
}

export async function fetchSalesStats(): Promise<SalesStats> {
  return api.get<SalesStats>('/v1/sales/stats')
}

export async function fetchSalesReports(): Promise<SalesReports> {
  return api.get<SalesReports>('/v1/sales/reports')
}

// ── Stage-probability ladder (2.2, P2-7) ──────────────────────────────────────

export async function fetchStageProbabilities(): Promise<SalesStageProbabilities> {
  return api.get<SalesStageProbabilities>('/v1/sales/settings/stage-probabilities')
}

/** PUT — only the stages being changed; null resets a stage to the platform default. */
export async function updateStageProbabilities(stages: Record<string, number | null>): Promise<SalesStageProbabilities> {
  const body: UpdateSalesStageProbabilitiesRequest = { stages }
  return api.put<SalesStageProbabilities>('/v1/sales/settings/stage-probabilities', body)
}

// ── Quotas + the Forecast grid (2.3b, F-4 / F-6) ──────────────────────────────
// One period model for all four: {periodKind, periodStart} as the server's
// period.go defines it — a UTC calendar month/quarter named by its first day.
// The client always sends BOTH explicitly (salesPeriod.ts derives them) so
// the label on screen and the window the server computed can never differ.

export type SalesPeriodQuery = { periodKind: SalesPeriodKind; periodStart: string }

/** GET — the quota rows that EXIST for the period (any member). */
export async function fetchSalesQuotas(period: SalesPeriodQuery): Promise<SalesQuotas> {
  return api.get<SalesQuotas>('/v1/sales/quotas', { params: period })
}

/** PUT — sets ONE employee's target for ONE period (owner/admin). A 400 here
 *  is an instruction (which date to send, which verb to use instead) — the
 *  card surfaces the sentence, never replaces it (U-8). */
export async function upsertSalesQuota(body: UpsertSalesQuotaRequest): Promise<SalesQuota> {
  return api.put<SalesQuota>('/v1/sales/quotas', body)
}

/** DELETE — clears a target by its natural key, carried in the BODY (owner/
 *  admin). 404 when there was nothing to clear. */
export async function deleteSalesQuota(body: DeleteSalesQuotaRequest): Promise<void> {
  await api.delete('/v1/sales/quotas', body)
}

/** PUT — sets or changes the governed forecast override on a deal (owner/
 *  admin, 2.3c P2-11). Returns the deal read with the override attached. A
 *  400/409 here is a sentence the row surfaces verbatim (U-8). */
export async function upsertForecastOverride(dealId: string, body: UpsertForecastOverrideRequest): Promise<DealDetail> {
  const d = await api.put<WireDeal>(`/v1/sales/deals/${dealId}/forecast-override`, body)
  return mapDealDetail(d)
}

/** DELETE — removes the override so the deal's own view stands again
 *  (owner/admin). 404 when there was none to remove. */
export async function deleteForecastOverride(dealId: string): Promise<DealDetail> {
  const d = await api.delete<WireDeal>(`/v1/sales/deals/${dealId}/forecast-override`)
  return mapDealDetail(d)
}

/** POST — the report builder's screen read (2.5, any member): groups capped
 *  at 200 with the exact group count and totals over ALL groups. A 400 is a
 *  sentence naming the slug the whitelist refused — surface it verbatim. */
export async function querySalesReport(def: SalesReportDefinition): Promise<SalesReportResult> {
  return api.post<SalesReportResult>('/v1/sales/reports/query', def)
}

/** POST — the UNCAPPED CSV of the same definition (2.5, any member). The
 *  server records the export in data_exports BEFORE streaming a byte and
 *  completes the row after the last (§20.6 Q2b); the response carries the
 *  file plus X-Report-Groups / X-Report-Rows / X-Report-Excluded-NonGbp and
 *  the Content-Disposition filename. Returns the blob and those facts so the
 *  caller can hand the file to the browser and state what it holds. */
export interface SalesReportCSV {
  blob: Blob
  filename: string
  groups: number
  rows: number
  excludedNonGbp: number
}
/** GET /v1/exports — the export audit, owner/admin (§31): one keyset page,
 *  newest first, with the exact total; `cursor` is a previous page's
 *  nextCursor. A viewer's 403 names the record and the role — but the
 *  Exports tab never sends this for a non-owner/admin (E-2), so the
 *  refusal is the boundary, not the render. */
export async function fetchDataExports(params: { limit?: number; cursor?: string } = {}): Promise<DataExportList> {
  const q: Record<string, string> = {}
  if (params.limit) q.limit = String(params.limit)
  if (params.cursor) q.cursor = params.cursor
  return api.get<DataExportList>('/v1/exports', { params: q })
}

export async function exportSalesReportCSV(def: SalesReportDefinition): Promise<SalesReportCSV> {
  const res = await apiFetch('/v1/sales/reports/query/csv', { method: 'POST', body: JSON.stringify(def) })
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const m = /filename="([^"]+)"/.exec(disposition)
  return {
    blob: await res.blob(),
    filename: m?.[1] ?? 'sales-report.csv',
    groups: Number(res.headers.get('X-Report-Groups') ?? 0),
    rows: Number(res.headers.get('X-Report-Rows') ?? 0),
    excludedNonGbp: Number(res.headers.get('X-Report-Excluded-NonGbp') ?? 0),
  }
}

/** GET — the whole Forecast tab in one read (any member, the /reports posture). */
export async function fetchSalesForecast(period: SalesPeriodQuery): Promise<SalesForecast> {
  return api.get<SalesForecast>('/v1/sales/forecast', { params: period })
}

/** Derives the Sales dashboard summary from real stats + the deals list +
 *  the reports aggregates. Email-funnel has no endpoint → honest zeros. */
export async function fetchSalesSummary(): Promise<{ summary: SalesSummary; deals: SalesDeal[]; activities: SalesActivity[] }> {
  const [stats, reports, deals, activities] = await Promise.all([fetchSalesStats(), fetchSalesReports(), fetchDeals(), fetchActivities()])
  const won = deals.filter((d) => d.stage === 'Closed Won')
  const lost = deals.filter((d) => d.stage === 'Closed Lost')
  const decided = won.length + lost.length
  const winRate = decided > 0 ? Math.round((won.length / decided) * 100) : 0
  // Summary money is integer PENCE throughout. Pipeline figures come from
  // /v1/sales/reports (R6-4): server-computed over the FULL dataset on the
  // open-stage basis — the /stats totalPipelineValue includes closed_won, and
  // the old list-derived mean mixed that numerator with an open denominator.
  const summary: SalesSummary = {
    totalPipeline: reports.totalPipelinePence,
    // 2.2 (P2-8): the effective-probability weighting from /reports, born
    // pence. Not the /stats float: since 0122 an unassessed deal's probability
    // is NULL there and its weight vanishes from that SUM. Absent on a
    // pre-2.2 backend → null → the card renders "—".
    weightedPipeline: reports.weightedPipelinePence ?? null,
    closedWon: won.reduce((s, d) => s + d.amount, 0),
    closedLost: lost.reduce((s, d) => s + d.amount, 0),
    totalDeals: stats.pipeline?.totalDeals ?? deals.length,
    openDeals: reports.openDealCount,
    openExcludedNonGbp: reports.openExcludedNonGbp,
    avgDealSize: reports.avgDealSizePence,
    winRate,
    dealsWinRate: `${winRate}%`,
    // SB-12: the server closed-cohort trio, passed through verbatim (absent
    // on pre-SB-12 backends — consumers must render "—", never a fake 0%).
    winRateClosedPct: reports.winRateClosedPct,
    wonCount: reports.wonCount,
    lostCount: reports.lostCount,
    // No email/outreach endpoint — honest zeros.
    emailsSent: 0,
    emailsDelivered: 0,
    emailsOpened: 0,
    emailsReplied: 0,
    totalRecipients: 0,
    monthlyRevenue: reports.revenueByMonth.map((m) => ({ month: m.month, won: m.wonPence })),
    dealsByStage: (stats.pipeline?.stages ?? []).map((s) => ({
      stage: SLUG_TO_STAGE[s.stage] ?? s.stage,
      count: s.count,
      value: Math.round((s.totalValue ?? 0) * 100), // pounds → pence, same boundary rule
    })),
    // Passed through verbatim (P10): the server's zero-filled 5-row spine —
    // the Dashboard stage rows read THIS, never dealsByStage, so empty
    // stages still render.
    funnel: reports.funnel ?? [],
    // 2.1b: the trend wire, verbatim — never a client-derived prior. Both
    // undefined on a pre-2.1b backend (no line renders, nothing to hide).
    historySince: reports.historySince,
    deltas: reports.deltas,
  }
  return { summary, deals, activities }
}

export async function fetchAgents(): Promise<DashAgent[]> {
  const r = await api.get<ListResp<Agent>>('/v1/agents')
  return (r.items ?? []).map((a) => ({
    id: a.id,
    name: a.name,
    status: 'active', // platform Agent has no status field; treat listed agents as active
    model: '—',
    schedule: 'On demand',
    lastRun: relAgo(a.updatedAt),
    successRate: 0, // no per-agent success metric endpoint
  }))
}

// Wire shape mirrors pkg/contracts Schedule json tags — the field is
// scheduleExpr (never cronExpr; that was a mock-only invention) and agentId is
// a UUID, not a name.
export interface ScheduleWire {
  id: string
  agentId?: string
  name?: string
  scheduleExpr?: string
  status?: string
  nextRunAt?: string
  lastRunAt?: string
}

/** Pure mapper (exported for tests). The list endpoint carries agent UUIDs
 *  only, so names are joined from /v1/agents; a schedule whose agent is
 *  missing from the list falls back to a short UUID prefix. */
export function toDashSchedules(items: ScheduleWire[], agents: Agent[]): DashSchedule[] {
  const names = new Map(agents.map((a) => [a.id, a.name]))
  return items.map((s) => ({
    id: s.id,
    agentId: s.agentId ?? '',
    agentName: names.get(s.agentId ?? '') ?? (s.agentId ? s.agentId.slice(0, 8) : '—'),
    name: s.name ?? '',
    expr: s.scheduleExpr ?? '—',
    nextRun: relIn(s.nextRunAt),
    lastRun: relAgo(s.lastRunAt),
    status: s.status ?? 'active',
  }))
}

export async function fetchSchedules(): Promise<DashSchedule[]> {
  const [r, agents] = await Promise.all([
    api.get<ListResp<ScheduleWire>>('/v1/schedules'),
    api.get<ListResp<Agent>>('/v1/agents'),
  ])
  return toDashSchedules(r.items ?? [], agents.items ?? [])
}

/** Enable/disable a schedule. The backend resets next_run_at to NULL on any
 *  PATCH; the scheduler re-seeds it within one poll tick, so a freshly
 *  enabled row briefly shows "—" for Next Run In. */
export function updateScheduleStatus(id: string, status: 'active' | 'disabled'): Promise<unknown> {
  return api.patch(`/v1/schedules/${encodeURIComponent(id)}`, { status })
}

/** Create a schedule for an agent. Always kind "cron" and timezone "UTC" —
 *  the runner evaluates UTC only (UTC-explicit ruling, slice C), and cron is
 *  the only executable kind. New schedules start active with next_run_at
 *  NULL; the runner seeds the first occurrence so creation never fires
 *  immediately. */
export function createSchedule(agentId: string, req: { name: string; scheduleExpr: string }): Promise<ScheduleWire> {
  return api.post(`/v1/agents/${encodeURIComponent(agentId)}/schedules`, {
    name: req.name,
    scheduleKind: 'cron',
    scheduleExpr: req.scheduleExpr,
    timezone: 'UTC',
  })
}

/** Change a schedule's expression (status untouched; next_run_at re-seeds). */
export function updateScheduleExpr(id: string, scheduleExpr: string): Promise<unknown> {
  return api.patch(`/v1/schedules/${encodeURIComponent(id)}`, { scheduleExpr })
}

export async function fetchRuns(limit = 20): Promise<DashRun[]> {
  const r = await api.get<RunsListResponse>('/v1/runs', { params: { limit } })
  const runs = r.runs ?? r.items ?? []
  return runs.map((run: Run) => ({
    id: run.id,
    agentName: run.agentName ?? run.agentId,
    status: run.status,
    held: run.held === true,
    agentStatus: run.agentStatus ?? '',
    heldSince: run.heldSince ?? undefined,
    startedAt: run.heldSince ?? run.startedAt ?? run.createdAt,
    duration: run.held ? 'Held' : durationBetween(run.startedAt ?? run.createdAt, run.finishedAt),
    currentStep: run.held
      ? `Held — agent ${run.agentStatus}`
      : run.status === 'waiting' || run.status === 'waiting_for_approval' ? 'Awaiting approval gate' : run.status,
    trigger: run.triggerType ?? 'manual',
  }))
}

export interface ScrapingDay {
  date: string; ok: number; blocked: number; error: number; total: number; successRate: number
}
export interface ScrapingHealth {
  days: ScrapingDay[]
  summary: ScrapingDay
}

interface WireScrapingDay { date: string; ok: number; blocked: number; error: number; total: number; success_rate: number }

export async function fetchScrapingHealth(days = 7): Promise<ScrapingHealth> {
  const r = await api.get<{ days?: WireScrapingDay[]; summary?: WireScrapingDay }>(
    '/v1/analytics/scraping-health', { params: { days } },
  )
  const map = (d: WireScrapingDay): ScrapingDay => ({
    date: d.date, ok: d.ok, blocked: d.blocked, error: d.error, total: d.total, successRate: d.success_rate,
  })
  const empty: ScrapingDay = { date: '', ok: 0, blocked: 0, error: 0, total: 0, successRate: 0 }
  return {
    days: (r.days ?? []).map(map),
    summary: r.summary ? map(r.summary) : empty,
  }
}

export interface LinkedInConnection { connected: boolean; expiresAt: string | null }

export async function fetchLinkedInConnection(): Promise<LinkedInConnection> {
  const r = await api.get<{ linkedin?: { connected?: boolean; expires_at?: string | null } }>('/v1/connections')
  return { connected: Boolean(r.linkedin?.connected), expiresAt: r.linkedin?.expires_at ?? null }
}

export interface RunPerformance {
  daily: { date: string; total: number; succeeded: number; failed: number }[]
  agents: { name: string; total: number; succeeded: number; failed: number; avgDuration: string; successRate: number }[]
}

/** Derives a 10-day run-volume series + per-agent breakdown from the raw runs
 *  list. There is no dedicated analytics endpoint, so everything here is computed
 *  client-side from real run records. */
export async function fetchRunPerformance(): Promise<RunPerformance> {
  const r = await api.get<RunsListResponse>('/v1/runs', { params: { limit: 500 } })
  const runs = (r.runs ?? r.items ?? []) as Run[]

  // 10-day daily buckets (oldest → newest).
  const days: { date: string; key: string; total: number; succeeded: number; failed: number }[] = []
  for (let i = 9; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000)
    days.push({
      date: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      key: d.toISOString().slice(0, 10),
      total: 0, succeeded: 0, failed: 0,
    })
  }
  const byKey = new Map(days.map((d) => [d.key, d]))

  // Per-agent aggregates.
  const agents = new Map<string, { name: string; total: number; succeeded: number; failed: number; durMs: number; durN: number }>()

  for (const run of runs) {
    const key = (run.startedAt ?? run.createdAt ?? '').slice(0, 10)
    const bucket = byKey.get(key)
    if (bucket) {
      bucket.total++
      if (run.status === 'succeeded') bucket.succeeded++
      else if (run.status === 'failed' || run.status === 'timed_out') bucket.failed++
    }
    const name = run.agentName ?? run.agentId
    const a = agents.get(name) ?? { name, total: 0, succeeded: 0, failed: 0, durMs: 0, durN: 0 }
    a.total++
    if (run.status === 'succeeded') a.succeeded++
    else if (run.status === 'failed' || run.status === 'timed_out') a.failed++
    if (run.startedAt && run.finishedAt) {
      const ms = new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
      if (ms > 0) { a.durMs += ms; a.durN++ }
    }
    agents.set(name, a)
  }

  return {
    daily: days.map(({ date, total, succeeded, failed }) => ({ date, total, succeeded, failed })),
    agents: [...agents.values()].map((a) => {
      const avgSec = a.durN > 0 ? Math.round(a.durMs / a.durN / 1000) : 0
      return {
        name: a.name,
        total: a.total,
        succeeded: a.succeeded,
        failed: a.failed,
        avgDuration: a.durN > 0 ? `${Math.floor(avgSec / 60)}m ${String(avgSec % 60).padStart(2, '0')}s` : '—',
        successRate: a.total > 0 ? Math.round((a.succeeded / a.total) * 100) : 0,
      }
    }),
  }
}

// SB-8: the resolved /v1/me identity, cached module-level so SYNCHRONOUS
// consumers (the draft key's lazy initial-state read) can use it. Populated
// by the Dashboard's boot fetch — by the time any drawer opens it is long
// resolved. While null (the pre-resolution window, or a failed /me), draft
// code neither reads nor writes: no draft is better than one under the
// wrong key.
let cachedMe: MeResponse | null = null

export function cachedMeIdentity(): { tenantId: string; subject: string } | null {
  return cachedMe ? { tenantId: cachedMe.tenantId, subject: cachedMe.subject } : null
}

export async function fetchMe(): Promise<MeResponse> {
  const me = await api.get<MeResponse>('/v1/me')
  cachedMe = me
  return me
}

/** The approvals status vocabulary — the 0-migration CHECK verbatim
 *  (pending/approved/rejected/edited/expired). `edited` is a real decided
 *  state (approved with an edited payload), so any "everything" read must
 *  include it or those rows stay invisible. */
export const APPROVAL_STATUSES = ['pending', 'approved', 'rejected', 'expired', 'edited'] as const
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number]

/** GET /v1/approvals for ONE status. The default is 'pending' because the
 *  SERVER's default is: the handler rewrites an empty ?status= to 'pending'
 *  (approval_handler.go), so there is no value that means "every status" —
 *  see fetchApprovalsEveryStatus. */
export async function fetchApprovals(status: ApprovalStatus = 'pending'): Promise<DashApproval[]> {
  const r = await api.get<ListResp<ApprovalGate>>('/v1/approvals', { params: { status, limit: 200 } })
  return (r.items ?? []).map((a) => ({
    id: a.id,
    runId: a.runId,
    agentName: a.skillId ?? a.runId,
    skill: a.skillId ?? '—',
    requestedAt: a.requestedAt,
    summary: typeof a.payload?.summary === 'string' ? a.payload.summary : 'Approval requested',
    payload: a.payload,
    status: a.status,
    expiresAt: a.expiresAt,
  }))
}

/** Every approval, whatever its status — a FAN-OUT across the vocabulary
 *  because the endpoint cannot express "all" (an empty ?status= is rewritten
 *  to 'pending' server-side). One call per status, in parallel; ids are
 *  unique per row so the concatenation needs no dedup. */
export async function fetchApprovalsEveryStatus(): Promise<DashApproval[]> {
  const perStatus = await Promise.all(APPROVAL_STATUSES.map(s => fetchApprovals(s)))
  return perStatus.flat()
}

export async function fetchRejectedApprovals(): Promise<DashApproval[]> {
  const r = await api.get<ListResp<ApprovalGate>>('/v1/approvals', { params: { status: 'rejected', limit: 200 } })
  return (r.items ?? []).map((a) => ({
    id: a.id,
    runId: a.runId,
    agentName: a.skillId ?? a.runId,
    skill: a.skillId ?? '—',
    requestedAt: a.requestedAt,
    summary: typeof a.payload?.summary === 'string' ? a.payload.summary : 'Approval rejected',
    payload: a.payload,
    status: a.status,
    expiresAt: a.expiresAt,
  }))
}

// The rejection justification lives on the RUN, not the approval: DecideApproval
// writes it to runs.error_message via CommandTransitionRunStatus. Rejections
// predating that field return the server's default sentence — honest, not a
// placeholder. Response typed inline so no new import is needed here.
export async function fetchRejectionReason(runId: string): Promise<string | null> {
  const r = await api.get<{ errorMessage?: string | null }>(`/v1/runs/${encodeURIComponent(runId)}`)
  return r.errorMessage ?? null
}

export async function decideApproval(
  id: string,
  status: 'approved' | 'rejected',
  editedPayload?: Record<string, unknown>,
  reason?: string,
): Promise<void> {
  await api.post<unknown>(`/v1/approvals/${encodeURIComponent(id)}/decision`, {
    status,
    ...(status === 'approved' && editedPayload && Object.keys(editedPayload).length > 0
      ? { editedPayload }
      : {}),
    ...(status === 'rejected' && reason && reason.trim() !== ''
      ? { reason: reason.trim() }
      : {}),
  })
}

// ── HR module ───────────────────────────────────────────────────────────────────

export async function fetchHRStats(): Promise<CandidateStats> {
  return api.get<CandidateStats>('/v1/hr/stats')
}

export async function fetchCandidates(params: Record<string, string> = {}): Promise<ApiHRCandidate[]> {
  const r = await api.get<ListResp<ApiHRCandidate>>('/v1/hr/candidates', { params: { limit: 200, ...params } })
  return r.items ?? []
}

export async function createCandidate(body: CreateCandidateRequest): Promise<ApiHRCandidate> {
  return api.post<ApiHRCandidate>('/v1/hr/candidates', body)
}

export async function fetchJobPostings(): Promise<ApiHRJobPosting[]> {
  const r = await api.get<ListResp<ApiHRJobPosting>>('/v1/hr/job-postings', { params: { limit: 200 } })
  return r.items ?? []
}

export async function shortlistCandidate(id: string): Promise<ApiHRCandidate> {
  return api.post<ApiHRCandidate>(`/v1/hr/candidates/${id}/shortlist`, {})
}

export async function rejectCandidate(id: string): Promise<ApiHRCandidate> {
  return api.post<ApiHRCandidate>(`/v1/hr/candidates/${id}/reject`, {})
}

export async function fetchTickets(params: Record<string, string> = {}): Promise<HRTicket[]> {
  const r = await api.get<ListResp<HRTicket>>('/v1/hr/tickets', { params: { limit: 200, ...params } })
  return r.items ?? []
}

export async function updateTicketStatus(id: string, status: string): Promise<HRTicket> {
  return api.patch<HRTicket>(`/v1/hr/tickets/${id}/status`, { status })
}

// ── Employees (tables 0069) ─────────────────────────────────────────────────────
// NOTE: unlike the older HR endpoints above, /v1/hr/employees returns BARE JSON
// arrays — there is NO {items} ListResp wrapper here. Do not "fix" these to
// unwrap; they would silently return undefined.

export async function fetchEmployees(params: Record<string, string> = {}): Promise<ApiEmployee[]> {
  return api.get<ApiEmployee[]>('/v1/hr/employees', { params: { limit: 200, ...params } })
}

// The NARROW people directory (AUTH-1): every non-HR consumer — pickers, the
// org chart, the Field Team, Lifecycle, the pay-run panel — reads THIS. Only
// the HR Employees list reads the fat fetchEmployees above.
export async function fetchDirectory(params: Record<string, string> = {}): Promise<ApiEmployeeDirectoryRow[]> {
  return api.get<ApiEmployeeDirectoryRow[]>('/v1/hr/employees/directory', { params: { limit: 200, ...params } })
}

export async function createEmployee(body: CreateEmployeeRequest): Promise<ApiEmployee> {
  return api.post<ApiEmployee>('/v1/hr/employees', body)
}

export async function fetchEmployee(id: string): Promise<ApiEmployee> {
  return api.get<ApiEmployee>(`/v1/hr/employees/${id}`)
}

export async function fetchEmployeeProfiles(id: string): Promise<ApiPayrollProfile[]> {
  return api.get<ApiPayrollProfile[]>(`/v1/hr/employees/${id}/profiles`)
}

export async function addPayrollProfile(id: string, body: AddPayrollProfileRequest): Promise<ApiPayrollProfile> {
  return api.post<ApiPayrollProfile>(`/v1/hr/employees/${id}/profiles`, body)
}

// The version in force on a date (YYYY-MM-DD; omit for today). 404s when no
// window covers the date — a gap is an answer, not a fetch failure.
export async function resolvePayrollProfile(id: string, on?: string): Promise<ApiPayrollProfile> {
  return api.get<ApiPayrollProfile>(`/v1/hr/employees/${id}/profile`, on ? { params: { on } } : undefined)
}

// Read-only Class 1 NI preview — nothing persists. 404 = no profile in force
// on the date; 422 = the module cannot calculate (no ruleset / unknown
// category / unmapped frequency) and its message names the cause. Slice 1
// calls with the id only; `on` and `grossPence` are here so slice 2 needs no
// change.
// The three cumulative figures ride the same optional idiom as gross: absent
// means "not supplied", which on a cumulative-basis record is what produces
// the cumulative_priors_required degrade rather than a different-basis answer.
// Zero is a REAL value (a first period), so undefined is the only absence.
export interface NIPreviewPriors {
  periodNumber?: number
  priorPayToDatePence?: number
  priorTaxToDatePence?: number
}

export async function fetchNIPreview(
  id: string, on?: string, grossPence?: number, priors?: NIPreviewPriors,
): Promise<NIPreview> {
  const params: Record<string, string | number> = {}
  if (on) params.on = on
  if (grossPence !== undefined) params.gross = grossPence
  if (priors?.periodNumber !== undefined) params.periodNumber = priors.periodNumber
  if (priors?.priorPayToDatePence !== undefined) params.priorPayToDate = priors.priorPayToDatePence
  if (priors?.priorTaxToDatePence !== undefined) params.priorTaxToDate = priors.priorTaxToDatePence
  return api.get<NIPreview>(`/v1/hr/employees/${id}/ni-preview`,
    Object.keys(params).length ? { params } : undefined)
}

// ── Org structure (E360-1 tables 0114; write surface E360-4) ────────────────────
// Same handler family as /v1/hr/employees: BARE JSON, no {items} wrapper.

// Active departments only by default — exactly what a picker wants. The
// authoring flag (?includeInactive=1) has no caller yet, on purpose.
export async function fetchOrgDepartments(): Promise<ApiOrgDepartment[]> {
  return api.get<ApiOrgDepartment[]>('/v1/hr/departments')
}

// Full version history, newest effective_from first.
export async function fetchOrgProfiles(id: string): Promise<ApiOrgProfile[]> {
  return api.get<ApiOrgProfile[]>(`/v1/hr/employees/${id}/org-profiles`)
}

export async function addOrgProfile(id: string, body: AddOrgProfileRequest): Promise<ApiOrgProfile> {
  return api.post<ApiOrgProfile>(`/v1/hr/employees/${id}/org-profiles`, body)
}

// FULL-REPLACE of phone/region/managerId — see UpdateEmployeeIdentityRequest
// for why all three are required. Returns the refreshed employee.
export async function updateEmployeeIdentity(id: string, body: UpdateEmployeeIdentityRequest): Promise<ApiEmployee> {
  return api.put<ApiEmployee>(`/v1/hr/employees/${id}/identity`, body)
}

// ── Learning providers (tables 0072/0073) ───────────────────────────────────────
// Like /v1/hr/employees these endpoints return BARE JSON — there is NO {items}
// ListResp wrapper. Do not "fix" these to unwrap; `r.items ?? []` would
// silently yield [].

export async function listProviders(params: Record<string, string> = {}): Promise<LearningProvider[]> {
  return api.get<LearningProvider[]>('/v1/hr/providers', { params: { limit: 200, ...params } })
}

export async function getProvider(id: string): Promise<LearningProvider> {
  return api.get<LearningProvider>(`/v1/hr/providers/${encodeURIComponent(id)}`)
}

export async function createProvider(body: CreateProviderRequest): Promise<LearningProvider> {
  return api.post<LearningProvider>('/v1/hr/providers', body)
}

export async function updateProvider(id: string, body: UpdateProviderRequest): Promise<LearningProvider> {
  return api.put<LearningProvider>(`/v1/hr/providers/${encodeURIComponent(id)}`, body)
}

export async function setProviderStatus(id: string, status: 'active' | 'inactive'): Promise<LearningProvider> {
  return api.post<LearningProvider>(`/v1/hr/providers/${encodeURIComponent(id)}/status`, { status })
}

/** A 404 here means the PROVIDER is missing — a real error, deliberately
 *  distinct from a provider with no standards yet (an empty array). */
export async function listStandards(providerId: string): Promise<LearningProviderStandard[]> {
  return api.get<LearningProviderStandard[]>(`/v1/hr/providers/${encodeURIComponent(providerId)}/standards`)
}

export async function addStandard(providerId: string, body: AddStandardRequest): Promise<LearningProviderStandard> {
  return api.post<LearningProviderStandard>(`/v1/hr/providers/${encodeURIComponent(providerId)}/standards`, body)
}

/** 204 No Content on success. */
export async function deleteStandard(providerId: string, standardId: string): Promise<void> {
  await api.delete(`/v1/hr/providers/${encodeURIComponent(providerId)}/standards/${encodeURIComponent(standardId)}`)
}

// ── Finance module ──────────────────────────────────────────────────────────────

export async function fetchFinanceStats(): Promise<FinanceStats> {
  return api.get<FinanceStats>('/v1/finance/stats')
}

export async function fetchInvoices(params: Record<string, string> = {}): Promise<FinanceInvoice[]> {
  const r = await api.get<ListResp<FinanceInvoice>>('/v1/finance/invoices', { params: { limit: 200, ...params } })
  return r.items ?? []
}

export async function fetchExpenses(params: Record<string, string> = {}): Promise<FinanceExpense[]> {
  const r = await api.get<ListResp<FinanceExpense>>('/v1/finance/expenses', { params: { limit: 200, ...params } })
  return r.items ?? []
}

/** The CAPTURABLE currency set (FX increment 3): GBP plus every quote with
 *  a fresh fixing — the pickers offer exactly what the write paths accept.
 *  Server-derived, never hardcoded client-side. */
export async function fetchFxCurrencies(): Promise<string[]> {
  const r = await api.get<ListResp<string>>('/v1/fx/currencies')
  return r.items ?? []
}

export async function approveInvoice(id: string): Promise<FinanceInvoice> {
  return api.post<FinanceInvoice>(`/v1/finance/invoices/${id}/approve`, {})
}

export async function rejectInvoice(id: string): Promise<FinanceInvoice> {
  return api.post<FinanceInvoice>(`/v1/finance/invoices/${id}/reject`, {})
}

export async function approveExpense(id: string): Promise<FinanceExpense> {
  return api.post<FinanceExpense>(`/v1/finance/expenses/${id}/approve`, {})
}

export async function rejectExpense(id: string): Promise<FinanceExpense> {
  return api.post<FinanceExpense>(`/v1/finance/expenses/${id}/reject`, {})
}

export async function createExpense(body: CreateExpenseRequest): Promise<FinanceExpense> {
  return api.post<FinanceExpense>('/v1/finance/expenses', body)
}

// ── Inbox triage module ─────────────────────────────────────────────────────────

/** GET /v1/inbound-emails returns a BARE array — no {items} wrapper (unlike the
 *  hr/finance lists above; `r.items ?? []` here would silently yield []). */
export async function fetchInboundEmails(status = 'pending_triage'): Promise<InboundEmail[]> {
  return api.get<InboundEmail[]>('/v1/inbound-emails', { params: { status } })
}

/** GET /v1/departments — bare array; active departments only by default. */
export async function fetchDepartments(): Promise<TenantDepartment[]> {
  return api.get<TenantDepartment[]>('/v1/departments')
}

/** Resolve = route the email to a department (the server dispatches its skill
 *  run, then flips the row to 'routed'). Non-2xx propagates as ApiError so the
 *  page can branch on err.status: 400 the picked department went stale / 404
 *  the row is no longer pending / 409 the department's skill requires a PDF
 *  this email cannot supply (the row stays pending_triage). */
export async function resolveInboundEmail(id: string, departmentId: string): Promise<InboundEmail> {
  return api.put<InboundEmail>(`/v1/inbound-emails/${id}/resolve`, { departmentId })
}

/** Discard = pure status flip (pending_triage → discarded), no dispatch. */
export async function discardInboundEmail(id: string): Promise<InboundEmail> {
  return api.put<InboundEmail>(`/v1/inbound-emails/${id}/discard`)
}

// ── AI enrichment ingest (PDF → run → extracted record) ─────────────────────────

interface IngestResponse { runId: string; status: string }

export async function ingestCV(pdfBase64: string, roleApplied?: string): Promise<IngestResponse> {
  return api.post<IngestResponse>('/v1/hr/candidates/ingest', { pdfBase64, roleApplied })
}

export async function ingestInvoicePDF(pdfBase64: string): Promise<IngestResponse> {
  return api.post<IngestResponse>('/v1/finance/invoices/ingest', { pdfBase64 })
}

// ── Tenant settings (invoice branding + issuing defaults) ───────────────────────

export async function fetchSettings(): Promise<TenantSettings> {
  return api.get<TenantSettings>('/v1/settings')
}

/** PUT /v1/settings — FULL-REPLACE upsert of the config fields (never the logo). */
export async function updateSettings(body: TenantSettingsUpdate): Promise<TenantSettings> {
  return api.put<TenantSettings>('/v1/settings', body)
}

/** PUT /v1/settings/logo — base64 image up; the backend enforces the png/jpeg/webp
 *  allowlist and a 2 MiB decoded cap. */
export async function uploadLogo(logoBase64: string, mime: string, fileName?: string): Promise<TenantSettings> {
  return api.put<TenantSettings>('/v1/settings/logo', { logoBase64, mime, fileName })
}

/** Fetch the logo as a blob and return an object URL. The API is Bearer-authed
 *  (no session cookie), so a bare <img src="/v1/settings/logo"> would 401 —
 *  callers render the object URL instead and own revoking it
 *  (URL.revokeObjectURL). Returns null when no logo is set (404) — and on any
 *  other fetch failure, since a missing preview must never break the page. */
export async function fetchLogoObjectUrl(): Promise<string | null> {
  try {
    const res = await apiFetch('/v1/settings/logo', { method: 'GET' })
    const blob = await res.blob()
    return URL.createObjectURL(blob)
  } catch (e) {
    if (e instanceof ApiError && e.status !== 404) {
      console.warn('logo fetch failed', e.status, e.statusText)
    }
    return null
  }
}

// ── Payroll ledger (per-period results; table 0080; PR 168 read routes) ────────

/** The pay-run view: every employee's CURRENT recorded row for one
 *  (tax year, period number), ordered by employee number. An unrecorded
 *  period or unknown tax year is a 200 with rows: [] — render the empty pay
 *  run, never an error. The server 400s (named prose, no SQLSTATE) on a
 *  non-integer or out-of-range period; the page bounds the input first. */
export async function fetchPayRun(taxYear: string, period: number): Promise<PayRun> {
  return api.get<PayRun>('/v1/payroll/pay-run', { params: { taxYear, period } })
}

/** The record action's DRY RUN (PR 172): the server computes the row in the
 *  same transaction the commit would use and rolls it back. Nothing is
 *  written, no idempotency key is reserved, and the returned figures are
 *  exactly what a commit with these inputs would store — which is why the
 *  gate renders THIS response and never a browser-side calculation. */
export async function previewPayrollPeriod(body: RecordPayrollPeriodRequest): Promise<RecordPayrollPeriodPreview> {
  return api.post<RecordPayrollPeriodPreview>('/v1/payroll/periods/preview', body)
}

/** The record action's COMMIT (PR 170). The Idempotency-Key header is the
 *  runs-precedent channel — the caller generates one key per preview and
 *  REUSES it on every retry of that same previewed intent, so a double-click
 *  or a network retry answers with the first write's row instead of a second
 *  one. A replay returns 200 with the existing row; a fresh write returns 201.
 *  Both statuses deserialise to the same shape, so the caller distinguishes
 *  them by asking the pay run, not by reading the status. */
export async function recordPayrollPeriod(
  body: RecordPayrollPeriodRequest,
  idempotencyKey: string,
): Promise<PayrollPeriodResult> {
  return api.post<PayrollPeriodResult>('/v1/payroll/periods', body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/** The correction's DRY RUN (PR 175): the server stamps, inserts, judges the
 *  series guard and rolls ALL of it back, answering with the replacement, the
 *  row it would supersede, and the chain depth it would create. A refusal the
 *  commit would hit — the named 404 (nothing recorded for that period), 409
 *  (client key reused) or 422 (the correction would drive the running tax
 *  total negative) — surfaces HERE, which is the point: the operator learns
 *  before committing, not after. */
export async function previewRestatePayrollPeriod(
  body: RestatePayrollPeriodRequest,
): Promise<RestatePayrollPeriodPreview> {
  return api.post<RestatePayrollPeriodPreview>('/v1/payroll/periods/restate/preview', body)
}

/** The correction's COMMIT (PR 175). Same Idempotency-Key discipline as the
 *  record path — one key per preview, reused on every retry of that intent.
 *
 *  Unlike recordPayrollPeriod this one reports the STATUS, because here the
 *  two answers mean different things to the operator: 201 means this request
 *  made the correction, 200 means the key had already been used and the row
 *  returned is the correction it made — nothing was stamped a second time and
 *  the chain did not grow. Saying "already applied" is honest where "corrected"
 *  would claim an act that did not happen. */
export async function restatePayrollPeriod(
  body: RestatePayrollPeriodRequest,
  idempotencyKey: string,
): Promise<{ row: PayrollPeriodResult; replayed: boolean }> {
  const res = await apiFetch('/v1/payroll/periods/restate', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return { row: await res.json() as PayrollPeriodResult, replayed: res.status === 200 }
}

// ── Payslips (tables 0084/0085/0086; PR 197 routes) ───────────────────────────

/** The stored documents for an employee's tax year, or for one row version.
 *  Metadata only — never the bytes. */
export async function fetchPayslips(params: {
  employeeId?: string
  taxYear?: string
  periodResultId?: string
}): Promise<Payslip[]> {
  const q = new URLSearchParams()
  if (params.employeeId) q.set('employeeId', params.employeeId)
  if (params.taxYear) q.set('taxYear', params.taxYear)
  if (params.periodResultId) q.set('periodResultId', params.periodResultId)
  const qs = q.toString()
  return api.get<Payslip[]>(`/v1/payroll/payslips${qs ? `?${qs}` : ''}`)
}

/** The issue gate's DRY RUN: the server runs the eligibility gate, renders the
 *  document, digests it, INSERTs and rolls ALL of it back — answering with the
 *  document that WOULD be issued and the metadata that would have been written.
 *
 *  A refusal the commit would hit surfaces HERE by name: the 422 for a
 *  superseded row or for absent s.8(2)(e) hours, the 409 for a version that
 *  already has a document. That is the point of previewing — the operator
 *  learns before committing, not after. */
export async function previewPayslip(periodResultId: string): Promise<PayslipPreview> {
  return api.post<PayslipPreview>('/v1/payroll/payslips/preview', { periodResultId })
}

/** The issue gate's COMMIT. Same Idempotency-Key discipline as the record and
 *  correction paths — ONE key per successful preview, reused on every retry of
 *  that same previewed intent, so a double-click or a network retry answers
 *  with the first document instead of refusing as a duplicate.
 *
 *  Reports the status because the two answers mean different things: 201 means
 *  this request issued the statement, 200 means the key had already been used
 *  and the document returned is the one it issued. */
export async function issuePayslip(
  periodResultId: string,
  idempotencyKey: string,
): Promise<{ preview: PayslipPreview; replayed: boolean }> {
  const res = await apiFetch('/v1/payroll/payslips', {
    method: 'POST',
    body: JSON.stringify({ periodResultId }),
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return { preview: await res.json() as PayslipPreview, replayed: res.status === 200 }
}

/** Fetch the STORED bytes. Retrieval, never a re-render — this is the only way
 *  the UI ever obtains an issued document, so what the operator sees is what
 *  was stored rather than what today's template would produce.
 *
 *  The caller owns the returned object URL and must revoke it
 *  (URL.revokeObjectURL). The digest and the bound version's status ride on
 *  response headers so a viewer can verify and decide the banner without a
 *  second request. */
export async function fetchPayslipDocument(id: string): Promise<PayslipDocument> {
  const res = await apiFetch(`/v1/payroll/payslips/${id}/document`, { method: 'GET' })
  const blob = await res.blob()
  const status = res.headers.get('X-Payslip-Period-Result-Status')
  return {
    blobUrl: URL.createObjectURL(blob),
    digestSha256: res.headers.get('X-Payslip-Digest-SHA256') ?? '',
    periodResultStatus: status === 'superseded' ? 'superseded' : 'current',
  }
}

/** Download the STORED bytes as a file. The banner a viewer shows for a
 *  superseded document is viewer CHROME — it is never written into the file,
 *  so a download yields the original bytes and the stored digest still
 *  verifies. That is the whole permanence guarantee (note §C.4). */
export async function downloadPayslipPdf(id: string, filename: string): Promise<void> {
  const res = await apiFetch(`/v1/payroll/payslips/${id}/document`, { method: 'GET' })
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

// ── Issued invoices (outbound AR; Phase 3b) ───────────────────────────────────
// NOTE: the list endpoint returns a BARE array (not the {items:[...]} envelope the
// AP fetchInvoices unwraps) — read it directly.
export async function fetchIssuedInvoices(params: Record<string, string> = {}): Promise<IssuedInvoice[]> {
  return api.get<IssuedInvoice[]>('/v1/finance/issued-invoices', { params: { limit: 200, ...params } })
}

export async function fetchNextInvoiceNumber(): Promise<NextInvoiceNumber> {
  return api.get<NextInvoiceNumber>('/v1/finance/issued-invoices/next-number')
}

export async function createIssuedInvoice(body: IssuedInvoiceCreate): Promise<IssuedInvoice> {
  return api.post<IssuedInvoice>('/v1/finance/issued-invoices', body)
}

export async function getIssuedInvoice(id: string): Promise<IssuedInvoice> {
  return api.get<IssuedInvoice>(`/v1/finance/issued-invoices/${id}`)
}

export async function updateIssuedInvoice(id: string, body: IssuedInvoiceCreate): Promise<IssuedInvoice> {
  return api.put<IssuedInvoice>(`/v1/finance/issued-invoices/${id}`, body)
}

export async function setIssuedInvoiceStatus(id: string, status: string): Promise<IssuedInvoice> {
  return api.post<IssuedInvoice>(`/v1/finance/issued-invoices/${id}/status`, { status })
}

export async function downloadIssuedInvoicePdf(id: string, invoiceNumber: string): Promise<void> {
  const res = await apiFetch(`/v1/finance/issued-invoices/${id}/pdf`, { method: 'GET' })
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = `${invoiceNumber}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

// ── Employment lifecycle (table 0092) + P45 (0093/0094) ───────────────────────
// Transition responses are the RAW Employment row (no derived status/current
// — those are the history read's derivation); callers re-fetch the history
// after a transition rather than merging. changedBy is NEVER a body field on
// any of these: the server stamps the authenticated session actor, and a body
// channel for it would let a caller sign someone else's name to an audit row.

/** The person's full employment history — every version, superseded included,
 *  each with the derived status/current pair. Bare array (no envelope). */
export async function fetchEmployments(employeeId: string): Promise<EmploymentHistoryRow[]> {
  return api.get<EmploymentHistoryRow[]>(`/v1/hr/employees/${employeeId}/employments`)
}

/** The leaver transition — EMPLOYEE-keyed (closes the person's open
 *  employment). One-shot: a wrong close is CORRECTED (superseded), never
 *  re-closed. */
export async function closeEmployment(
  employeeId: string,
  body: { leavingDate: string; changeReason: string },
): Promise<Employment> {
  return api.post<Employment>(`/v1/hr/employees/${employeeId}/employment/close`, body)
}

/** Correct a mistaken close — EMPLOYMENT-keyed (targets the version being
 *  corrected, which supersedes into a NEW row; nothing is rewritten).
 *  leavingDate OMITTED = the un-leave correction (the whole close was a
 *  mistake); an empty string is a malformed date the server refuses —
 *  absence must be said by omission. JSON.stringify drops undefined keys,
 *  which is exactly the wire shape the route wants. */
export async function correctEmployment(
  employmentId: string,
  body: { leavingDate?: string; changeReason: string },
): Promise<Employment> {
  return api.post<Employment>(`/v1/hr/employments/${employmentId}/correct`, body)
}

/** Open a new employment beside a closed one — EMPLOYEE-keyed. Refuses 409
 *  while an open employment exists ("close it first"). */
export async function rehireEmployee(
  employeeId: string,
  body: { startDate: string; changeReason: string },
): Promise<Employment> {
  return api.post<Employment>(`/v1/hr/employees/${employeeId}/employment/rehire`, body)
}

/** Discovery list: which certificates exist. Bare array of METADATA — the
 *  bytes live behind fetchP45Document only. Empty is empty ([]), never 404:
 *  an employment with no P45 is a normal answer about a collection. */
export async function fetchP45s(params: {
  employeeId?: string
  employmentId?: string
}): Promise<P45[]> {
  const q = new URLSearchParams()
  if (params.employeeId) q.set('employeeId', params.employeeId)
  if (params.employmentId) q.set('employmentId', params.employmentId)
  const qs = q.toString()
  return api.get<P45[]>(`/v1/hr/p45s${qs ? `?${qs}` : ''}`)
}

/** The issue gate's DRY RUN: gate + fold + render + INSERT, all rolled back,
 *  answering with the certificate that WOULD be issued (document as base64 —
 *  this arm is JSON). A refusal the commit would hit surfaces HERE by name:
 *  the 422 for a still-open employment, the 409 for a superseded version or
 *  an already-issued certificate. No body — everything a P45 needs derives
 *  from the employment. */
export async function previewP45(employmentId: string): Promise<P45Preview> {
  const res = await apiFetch(`/v1/hr/employments/${employmentId}/p45/preview`, { method: 'POST' })
  return await res.json() as P45Preview
}

/** The issue gate's COMMIT — the payslip Idempotency-Key discipline: ONE key
 *  per successful preview, reused on every retry of that same previewed
 *  intent. 201 = this request issued it; 200 = the key was already used and
 *  this is the certificate it issued. */
export async function issueP45(
  employmentId: string,
  idempotencyKey: string,
): Promise<{ preview: P45Preview; replayed: boolean }> {
  const res = await apiFetch(`/v1/hr/employments/${employmentId}/p45`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return { preview: await res.json() as P45Preview, replayed: res.status === 200 }
}

/** Fetch the STORED bytes. Retrieval, never a re-render — the only way the UI
 *  ever obtains an issued certificate, so what the operator sees is what was
 *  stored. Caller owns the object URL and must revoke it; the digest rides
 *  the X-P45-Digest-SHA256 header so a viewer can verify without a second
 *  request. */
export async function fetchP45Document(id: string): Promise<P45Document> {
  const res = await apiFetch(`/v1/hr/p45s/${id}/document`, { method: 'GET' })
  const blob = await res.blob()
  return {
    blobUrl: URL.createObjectURL(blob),
    digestSha256: res.headers.get('X-P45-Digest-SHA256') ?? '',
  }
}

/** ENQUEUE the send (202 always — a send is owed and recorded; the email has
 *  not been attempted). No body: recipient and key are derived server-side,
 *  so nothing a caller sends can change where a statutory document goes. A
 *  re-press replays the existing receipt (replayed=true) — safe, no second
 *  send. */
export async function sendP45(p45Id: string): Promise<{
  actionId: string
  status: string
  p45Id: string
  recipientEmail: string
  idempotencyKey: string
  replayed: boolean
}> {
  const res = await apiFetch(`/v1/hr/p45s/${p45Id}/send`, { method: 'POST' })
  return await res.json() as {
    actionId: string; status: string; p45Id: string
    recipientEmail: string; idempotencyKey: string; replayed: boolean
  }
}

/** The delivery receipt's state. status "none" = never sent — a 200 the UI
 *  renders as "not yet sent", never as a failure. */
export async function fetchP45SendStatus(id: string): Promise<P45SendStatus> {
  return api.get<P45SendStatus>(`/v1/hr/p45s/${id}/send-status`)
}

// ── HR Onboarding (ON-2: tables 0106, routes ON-1) ─────────────────────────────
// Wire = view (contracts.HROnboardingTemplate / HROnboarding verbatim). Both
// lists come back ListResponse-wrapped ({items}), UNLIKE the bare-array
// /v1/hr/employees above. Complete/uncomplete return the REFRESHED instance
// (both idempotent) — callers swap it into state, no refetch needed.

export async function fetchOnboardingTemplates(): Promise<OnboardingTemplate[]> {
  const r = await api.get<ListResp<OnboardingTemplate>>('/v1/hr/onboarding/templates', { params: { limit: 200 } })
  return r.items ?? []
}

export async function createOnboardingTemplate(body: OnboardingTemplateWrite): Promise<OnboardingTemplate> {
  return api.post<OnboardingTemplate>('/v1/hr/onboarding/templates', body)
}

export async function updateOnboardingTemplate(id: string, body: OnboardingTemplateWrite): Promise<OnboardingTemplate> {
  return api.put<OnboardingTemplate>(`/v1/hr/onboarding/templates/${encodeURIComponent(id)}`, body)
}

export async function deleteOnboardingTemplate(id: string): Promise<void> {
  await api.delete(`/v1/hr/onboarding/templates/${encodeURIComponent(id)}`)
}

export async function startOnboarding(employeeId: string, templateId: string): Promise<Onboarding> {
  return api.post<Onboarding>('/v1/hr/onboarding/start', { employeeId, templateId })
}

export async function fetchOnboarding(): Promise<Onboarding[]> {
  const r = await api.get<ListResp<Onboarding>>('/v1/hr/onboarding', { params: { limit: 200 } })
  return r.items ?? []
}

export async function completeOnboardingItem(itemId: string): Promise<Onboarding> {
  return api.post<Onboarding>(`/v1/hr/onboarding/items/${encodeURIComponent(itemId)}/complete`, {})
}

export async function uncompleteOnboardingItem(itemId: string): Promise<Onboarding> {
  return api.post<Onboarding>(`/v1/hr/onboarding/items/${encodeURIComponent(itemId)}/uncomplete`, {})
}

/** Fenced server-side: deleting a COMPLETE checklist is a named 400 (pin 5 —
 *  free while incomplete, the hire fell through). */
export async function deleteOnboarding(id: string): Promise<void> {
  await api.delete(`/v1/hr/onboarding/${encodeURIComponent(id)}`)
}

// ── Operations (M1: SOPs) ──────────────────────────────────────────────────────
// Wire = view (contracts.OpsSop verbatim); display formatting lives in
// pages/operations/sopsView.ts. Lists come back ListResponse-wrapped.

export async function fetchSops(): Promise<OpsSop[]> {
  const r = await api.get<ListResp<OpsSop>>('/v1/operations/sops', { params: { limit: 200 } })
  return r.items ?? []
}

export async function createSop(body: SopWrite): Promise<OpsSop> {
  return api.post<OpsSop>('/v1/operations/sops', body)
}

export async function updateSop(id: string, body: SopWrite): Promise<OpsSop> {
  return api.put<OpsSop>(`/v1/operations/sops/${encodeURIComponent(id)}`, body)
}

export async function deleteSop(id: string): Promise<void> {
  await api.delete(`/v1/operations/sops/${encodeURIComponent(id)}`)
}

// ── Operations (M2: Quality Checks) ────────────────────────────────────────────
// create + list + follow-up only — checks have no edit or delete routes.

export async function fetchQualityChecks(): Promise<OpsQualityCheck[]> {
  const r = await api.get<ListResp<OpsQualityCheck>>('/v1/operations/quality-checks', { params: { limit: 200 } })
  return r.items ?? []
}

export async function createQualityCheck(body: QualityCheckWrite): Promise<OpsQualityCheck> {
  return api.post<OpsQualityCheck>('/v1/operations/quality-checks', body)
}

export async function followUpQualityCheck(id: string): Promise<OpsQualityCheck> {
  return api.post<OpsQualityCheck>(`/v1/operations/quality-checks/${encodeURIComponent(id)}/follow-up`)
}

// ── Operations (M3: Incidents) ─────────────────────────────────────────────────
// create + list + resolve only — incidents have no edit or delete routes.

export async function fetchIncidents(): Promise<OpsIncident[]> {
  const r = await api.get<ListResp<OpsIncident>>('/v1/operations/incidents', { params: { limit: 200 } })
  return r.items ?? []
}

export async function createIncident(body: IncidentWrite): Promise<OpsIncident> {
  return api.post<OpsIncident>('/v1/operations/incidents', body)
}

export async function resolveIncident(id: string): Promise<OpsIncident> {
  return api.post<OpsIncident>(`/v1/operations/incidents/${encodeURIComponent(id)}/resolve`)
}

// ── Operations (M4: Assets) ────────────────────────────────────────────────────
// create + list + full-edit PUT (decision M4 — the register needs the
// reassign/status/retire path); no delete route — 'retired' is end-of-life.

export async function fetchAssets(): Promise<OpsAsset[]> {
  const r = await api.get<ListResp<OpsAsset>>('/v1/operations/assets', { params: { limit: 200 } })
  return r.items ?? []
}

export async function createAsset(body: AssetWrite): Promise<OpsAsset> {
  return api.post<OpsAsset>('/v1/operations/assets', body)
}

export async function updateAsset(id: string, body: AssetWrite): Promise<OpsAsset> {
  return api.put<OpsAsset>(`/v1/operations/assets/${encodeURIComponent(id)}`, body)
}

// ── Operations (M5a: Field Schedule tasks) ─────────────────────────────────────
// create + date-window list + full-edit PUT (reschedule = same PUT, new
// date) + the complete transition + delete (a completed task refuses with
// the server's named-400 fence — surface it via apiErrorMessage).

export async function fetchTasks(window: { from?: string; to?: string } = {}): Promise<OpsTask[]> {
  const params: Record<string, string | number> = { limit: 200 }
  if (window.from) params.from = window.from
  if (window.to) params.to = window.to
  const r = await api.get<ListResp<OpsTask>>('/v1/operations/tasks', { params })
  return r.items ?? []
}

export async function createTask(body: TaskWrite): Promise<OpsTask> {
  return api.post<OpsTask>('/v1/operations/tasks', body)
}

export async function updateTask(id: string, body: TaskWrite): Promise<OpsTask> {
  return api.put<OpsTask>(`/v1/operations/tasks/${encodeURIComponent(id)}`, body)
}

export async function completeTask(id: string): Promise<OpsTask> {
  return api.post<OpsTask>(`/v1/operations/tasks/${encodeURIComponent(id)}/complete`)
}

export async function deleteTask(id: string): Promise<void> {
  await api.delete(`/v1/operations/tasks/${encodeURIComponent(id)}`)
}
